import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import Navbar from '../navbar/navbar';
import { useNavigate } from 'react-router-dom';
import Peer from 'simple-peer';
import Swal from 'sweetalert2';
import { socket } from './../../socket';

const VoiceChat = () => {
  const navigate = useNavigate();

  const serverHost = process.env.REACT_APP_SERVER_HOST
  
  const myAudio = useRef(null);
  const userAudio = useRef(null);
  const connectionRef = useRef(null);
  const streamRef = useRef(null);
  const [matchedUser, updateMatchedUser] = useState({
    id: null,
    name: '',
    icon: ''
  });
  const [myUserImage, updateMyUserImage] = useState('');
  const [isMuted, setMute] = useState(false);

  const navigateHome = () => {
    navigate('/dashboard');
  };

  const toggleMute = (isMuted) => {
    if (myAudio.current) {
      const audioTracks = myAudio.current.srcObject.getAudioTracks();
      audioTracks.forEach(track => {
        track.enabled = !isMuted;
      });

      setMute(isMuted);
    }
  };

  const setSocketListeners = () => {
    socket.connect();

    socket.on('connect', () => {
      console.log("socket connected");
      socket.emit('connection', { "chat_type": "voice", "uid": parseInt(sessionStorage.getItem("user_id"), 10), "username": sessionStorage.getItem("username"), "icon": sessionStorage.getItem("icon") });
    });

    socket.on('disconnect', () => {
      console.log("socket disconnected");
    })

    socket.on('friend-req', (user) => {
      Swal.fire({
        title: 'Friend Request',
        text: `${user.username} wants to be your friend`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Accept',
        cancelButtonText: 'Deny',
      }).then(async (result) => {
        if (result.isConfirmed) {
          const formData = new FormData();
  
          formData.append('user_id', parseInt(sessionStorage.getItem("user_id"), 10));
          formData.append('friend_id', user.user_id);
  
          try {
            const response = await fetch(`${serverHost}/add-friend`, {
              method: 'POST',
              body: formData,
            });
  
            const result = await response.json();
  
            if (response.status === 200) {
              // Handle success, if needed
            } else {
              Swal.fire({
                title: 'Failed to add friend',
                text: result.error,
                icon: 'error',
                confirmButtonText: 'Close'
              });
            }
          } catch (error) {
            console.error('Error adding friend:', error);
          }
        }
      });
    });
    
    socket.on('reported', () => {
      Swal.fire({
        title: 'Warning',
        html: "You have been reported.<br>Multiple reports may result in deletion of your account.",
        icon: 'warning',
        confirmButtonText: 'Close'
      });
    });

    socket.on('match', (user) => {
      console.log("matched");

      updateMatchedUser({
        id: user.user_id,
        name: 'User',
        icon: user.icon
      });

      callUser();
    });

    socket.on('callUser', (data) => {
      console.log("call received");

      updateMatchedUser({
        id: data.match_id,
        name: 'User',
        icon: data.icon
      });

      answerCall(data.signal);
    });

    socket.on('unmatch', () => {
      window.location.reload();
      // leaveCall();
    });
  };

  const leaveCall = () => {
    if (connectionRef.current) {
      connectionRef.current.destroy();
    }
    if (userAudio.current) {
      userAudio.current.srcObject = null;
    }
    updateMatchedUser({
      id: null,
      name: '',
      icon: ''
    });
  };

  const callUser = () => {
    try {
      console.log("calling user");
      if (myAudio.current) {
        const peer = new Peer({
          initiator: true,
          trickle: false,
          stream: myAudio.current.srcObject,
        });

        console.log('new peer created', peer);

        peer.on('signal', (data) => {
          socket.emit('callUser', data);
        });

        peer.on('stream', (userStream) => {
          userAudio.current.srcObject = userStream;
        });

        socket.on('callAccepted', (signal) => {
          console.log("call accepted");
          peer.signal(signal);
        });

        connectionRef.current = peer;
      }
    } catch (error) {
      // console.error('Error calling user:', error);
    }
  };

  const answerCall = (callerData) => {
    try {
      console.log("answering call");
      if (myAudio.current) {
        const peer = new Peer({
          initiator: false,
          trickle: false,
          stream: myAudio.current.srcObject,
        });

        console.log('new peer created', peer);

        peer.on('signal', (data) => {
          socket.emit('answerCall', data);
        });

        peer.on('stream', (userStream) => {
          userAudio.current.srcObject = userStream;
        });

        peer.signal(callerData);
        connectionRef.current = peer;
      }
    } catch (error) {
      // console.error('Error answering call:', error);
    }
  };

  const shuffle = () => {
    window.location.reload();
    // leaveCall();
    // socket.emit('shuffleCall');
  };

  const report = () => {
    socket.emit('report');
    Swal.fire({
      title: 'User Reported',
      text: 'Thanks for keeping the community healthy 🙇',
      icon: 'success',
      confirmButtonText: 'Close'
    });
  }

  const addFriend = () => {
    socket.emit('send-request');
    Swal.fire({
      title: 'Request Sent',
      text: `Friendly ${matchedUser.name} will be notified of your friend request`,
      icon: 'success',
      confirmButtonText: 'Close'
    });
  }

  useEffect(() => {
    if (sessionStorage.getItem("user_id") === null) {
      console.log("Not Authenticated");
      navigate('/');
    }

    navigator.mediaDevices.getUserMedia({ audio: true })
      .then((userStream) => {
        streamRef.current = userStream;
        if (myAudio.current) {
          myAudio.current.srcObject = userStream;
        }

        setSocketListeners();
        updateMyUserImage(sessionStorage.getItem("icon"));
      })
      .catch(error => {
        Swal.fire({
          title: 'Failed to connect',
          text: error,
          icon: 'error',
          confirmButtonText: 'Close',
          onClose: navigate('/dashboard')
        });
        // console.error('Error accessing user media:', error);
      });

    return () => {
      leaveCall();
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      socket.disconnect();
      socket.removeAllListeners();
    };
  }, []);

  return (
    <>
      <video playsInline muted={true} ref={myAudio} autoPlay style={{ display: 'none' }} />
      <video playsInline ref={userAudio} autoPlay style={{ display: 'none' }} />
      <Navbar />
      <Container>
        <ChatSection>
          <Head>
            <img
              src="../images/close.png"
              alt="close"
              style={{ cursor: 'pointer' }}
              onClick={navigateHome}
            />
          </Head>
          <Chat>
            <AnonyCard>
              {matchedUser.id ?
                <>
                  <span>Friendly {matchedUser.name} <i style={{ color: 'green' }} className="fa fa-circle" /></span>
                  <img src={`avatars/${matchedUser.icon}.png`} alt="user icon" style={{ borderRadius: '50%' }} />
                  <UserControls>
                    <i style={{ color: 'green', cursor: "pointer" }} className="fa fa-user-plus" onClick={addFriend} title='Add Friend' />
                    <i style={{ color: 'red', cursor: "pointer" }} className="fa fa-flag" onClick={report} title='Report User' />
                    <img src="../images/shuffle.png" alt="shuffle" onClick={shuffle} title='Rematch' />
                  </UserControls>
                </>
                :
                <span style={{ margin: "auto", fontSize: "18px" }}>🔎 Finding someone for you</span>
              }
            </AnonyCard>
            <MyCard>
              <span>You</span>
              <img src={`avatars/${myUserImage}.png`} alt="user icon" style={{ borderRadius: '50%' }} />
              {matchedUser.id &&
                <UserControls>
                  {isMuted ?
                    <i className="fa fa-microphone-slash" onClick={() => toggleMute(false)} title='Unmute' /> :
                    <i className="fa fa-microphone" onClick={() => toggleMute(true)} style={{ color: "#6559a2" }} title='Mute' />
                  }
                </UserControls>
              }
            </MyCard>
          </Chat>
          <span style={{ "marginTop": "20px", "marginBottom": "12px" }}>Use headphones for a better experience 🎧</span>
        </ChatSection>
      </Container>
    </>
  );
}

export default VoiceChat;

const Container = styled.main`
  display: flex;
  align-items: center;
  height: 86vh;
  background-color: white;
  justify-content: space-around;

  @media only screen and (max-width: 600px) {
    height: 92vh;
    padding: 5% 0;
  }
`;

const ChatSection = styled.div`
  display: flex;
  width: 80vw;
  border-radius: 5px;
  background: rgba(217, 217, 217, 0.5);
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  flex-direction: column;
  text-align: center;

  @media only screen and (max-width: 600px) {
    width: 90%;
    margin: auto;
  }
`;

const Head = styled.div`
  text-align: right;
  padding: 10px 30px;
`;

const Chat = styled.div`
  display: flex;
  justify-content: space-around;

  @media only screen and (max-width: 600px) {
    flex-direction: column;
    gap: 10px;
    align-items: center;
  }
`;

const AnonyCard = styled.div`
  padding: 70px 0;
  display: flex;
  background: white;
  min-width: 40%;
  text-align: center;
  border-radius: 10px;
  flex-direction: column;
  gap: 40px;
  box-shadow: rgba(0, 0, 0, 0.2) -3px 3px 0px;

  span {
    font-size: 25px;
  }

  img {
    margin: 0 auto;
  }

  @media only screen and (max-width: 600px) {
    min-width: 90%;
    padding: 5% 5%;
    min-height: 30vh;
    gap: 10px;
    span {
      font-size: 15px;
    }
  }
`;

const MyCard = styled.div`
  padding: 70px 0;
  display: flex;
  background: white;
  min-width: 40%;
  text-align: center;
  border-radius: 10px;
  flex-direction: column;
  gap: 40px;
  box-shadow: rgba(0, 0, 0, 0.2) -3px 3px 0px;

  span {
    font-size: 25px;
  }

  img {
    margin: 15px auto;
  }

  i {
    font-size: 32px;
    cursor: pointer;
  }

  @media only screen and (max-width: 600px) {
    min-width: 90%;
    padding: 5% 5%;
    gap: 10px;
  }
`;

const UserControls = styled.div`
  margin-top: 10%;

  i {
    font-size: 32px;
    cursor: pointer;
    margin: auto 20px;
    // transition: color 0.3s ease;
  }

  img {
    margin-left: 20px;
    height: 32px !important;
    cursor: pointer;
  }
`;